import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import { GET_USERS, GET_PROJECTS, ACTIVATE_PROJECT } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import type { ViewName } from "@/types";

const NAV_ITEMS: { label: string; view: ViewName; icon: string }[] = [
  { label: "Dashboard", view: "dashboard", icon: "dashboard" },
  { label: "Requirements", view: "requirements", icon: "requirements" },
  { label: "Tests", view: "tests", icon: "tests" },
  { label: "Projects", view: "process", icon: "projects" },
  { label: "Settings", view: "settings", icon: "settings" },
];

function NavIcon({ name }: { name: string }) {
  switch (name) {
    case "dashboard":
      return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" /><rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" /></svg>;
    case "requirements":
      return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 3h10M3 6h10M3 9h7M3 12h5" /></svg>;
    case "tests":
      return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2v4l-2 2v6h8V8l-2-2V2" /><path d="M5 2h6" /></svg>;
    case "projects":
      return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4l5-2 5 2v8l-5 2-5-2z" /><path d="M7 4v10" /><path d="M2 4l5 2 5-2" /></svg>;
    case "settings":
      return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.9 2.9l1.4 1.4M11.7 11.7l1.4 1.4M13.1 2.9l-1.4 1.4M4.3 11.7l-1.4 1.4" /></svg>;
    default:
      return null;
  }
}

interface LayoutProps {
  children: React.ReactNode;
  authUser: { id: string; name: string; role: string };
  environment?: string;
  onLogout: () => void;
}

export function Layout({ children, authUser, environment = "", onLogout }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 960);
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 960);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 959px)");
    const handler = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const { currentView, setCurrentView, activeUser, setActiveUser, setAllUsers, activeProject, setActiveProject, setAuthRole, setAuthUserId, authRole, impersonatedRole } = useApp();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const client = useApolloClient();

  const { data: usersRaw } = useQuery<any>(GET_USERS);
  const { data: projectsRaw } = useQuery<any>(GET_PROJECTS);

  // Read projects directly from Apollo response (no mapToSnakeCase for header dropdown)
  const projectsList: { id: string; name: string }[] = projectsRaw?.projects?.projects ?? [];
  const apiActiveProject: string = projectsRaw?.projects?.activeProject ?? "";

  // Read users via mapToSnakeCase (needed for deeper structures)
  const usersData = usersRaw ? mapToSnakeCase(usersRaw.users) : null;

  const [activateProjectMutation] = useMutation<any>(ACTIVATE_PROJECT);

  useEffect(() => {
    if (apiActiveProject && !activeProject) {
      setActiveProject(apiActiveProject);
    } else if (activeProject && apiActiveProject && activeProject !== apiActiveProject) {
      // URL set a different project — activate it server-side
      activateProjectMutation({ variables: { projectId: activeProject } });
    }
  }, [apiActiveProject, activeProject, setActiveProject, activateProjectMutation]);

  useEffect(() => {
    if (usersData && !activeUser) {
      const active = usersData.users.find((u: { id: string }) => u.id === authUser.id) || usersData.users[0] || null;
      setActiveUser(active);
      setAllUsers(usersData.users);
      setAuthRole(authUser.role);
      setAuthUserId(authUser.id);
    }
  }, [usersData, authUser, activeUser, setActiveUser, setAllUsers, setAuthRole, setAuthUserId]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <nav className={`nav-sidebar${collapsed ? " collapsed" : ""}${!collapsed && isNarrow ? " expanded-overlay" : ""}`}>
        <div className="nav-logo">
          <img className="nav-logo-icon" src="/static/favicon.svg" alt="Argus Trace" />
          <span className="nav-logo-text">ArgusTrace</span>
        </div>
        <div className="nav-items">
          {NAV_ITEMS.map(item => (
            <button
              key={item.view}
              className={`nav-item${currentView === item.view ? " active" : ""}`}
              onClick={() => setCurrentView(item.view)}
              title={collapsed ? item.label : undefined}
            >
              <span className="nav-icon"><NavIcon name={item.icon} /></span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </div>
        <button className="nav-collapse-btn" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d={collapsed ? "M6 3l5 5-5 5" : "M10 3l-5 5 5 5"} />
          </svg>
          {!collapsed && <span className="nav-collapse-label">Collapse</span>}
        </button>
      </nav>

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="h-11 min-h-[44px] flex items-center bg-[var(--bg2)] border-b border-border px-5 gap-3 shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {environment === "staging" && (
              <span className="rounded-full border border-amber-500/60 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold tracking-[0.12em] text-amber-400">
                STAGING
              </span>
            )}
            <select
              className="px-2 py-[3px] bg-[var(--bg)] border border-[var(--border)] rounded-[var(--radius)] text-[var(--text)] text-[13px] font-semibold outline-none cursor-pointer min-w-[140px] max-w-60 focus:border-[var(--accent)]"
              value={activeProject || apiActiveProject}
              disabled={projectsList.length === 0}
              aria-label="Project"
              onChange={async (e) => {
                const newProject = e.target.value;
                setActiveProject(newProject);
                await activateProjectMutation({ variables: { projectId: newProject } });
                await client.resetStore();
              }}
            >
              {projectsList.length === 0 ? (
                <option value="">Loading projects…</option>
              ) : (
                <>
                  {projectsList.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || p.id}</option>
                  ))}
                  {authRole === "admin" && !impersonatedRole && <option value="__unlinked__">Unlinked</option>}
                </>
              )}
            </select>
          </div>
          {/* Profile / Logout */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center gap-2 bg-transparent border-none cursor-pointer text-[var(--text)] p-0"
                aria-label="Profile menu"
              >
                <div className="w-[26px] h-[26px] rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-[10px] font-bold shrink-0 cursor-pointer">
                  {authUser.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs text-[var(--text2)] whitespace-nowrap cursor-pointer">
                  <span className="text-[var(--text)] font-medium">{authUser.name}</span>
                  <span className="text-[10px] text-[var(--text2)]"> ({impersonatedRole ? `${impersonatedRole}` : authUser.role})</span>
                  {impersonatedRole && <span className="ml-1 px-1 py-0.5 text-[9px] bg-[var(--accent)] text-white rounded font-semibold">IMPERSONATING</span>}
                </span>
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-[var(--bg2)] border border-[var(--border)] rounded-[var(--radius)] shadow-[0_8px_24px_rgba(0,0,0,0.4)] py-1 z-[100]">
                  <div className="px-3.5 py-2 border-b border-[var(--border)]">
                    <div className="text-xs font-medium text-[var(--text)]">{authUser.name}</div>
                    <div className="text-[10px] text-[var(--text2)] mt-0.5">{authUser.role}</div>
                  </div>
                  <button
                     className="w-full text-left px-3.5 py-2 text-xs bg-transparent border-none cursor-pointer text-[var(--text2)] hover:bg-[var(--badge-muted-bg)] hover:text-[var(--text)] transition-[background,color] duration-100 flex items-center gap-2"
                    onClick={() => { setProfileMenuOpen(false); onLogout(); }}
                  >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3 h-3">
                      <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3M11 11l3-3-3-3M14 8H6" />
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex flex-1 overflow-hidden min-h-0 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
